import React, { useEffect, useState } from 'react'
import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts'
import { Info } from 'lucide-react'
import { useDispatch, useSelector } from 'react-redux'
import LanguageTranslator from '../../../../locals/LanguageTranslator'
import { dispatchHandler } from '../../../../helper/apiHelperFunctions'

const DONUT_COLORS = [
    "#5D5FEF", "#8E8FF4", "#B6B7F8", "#DFDFFC", "#EFEFFE", 
    "#7D7FF2", "#A6A7F6", "#CFCFFB", "#DFDFFC", "#F7F7FE"
];

// "Category Contribution" : donut on the left, a two column legend on the right. The legend is
// filled column first so the grid flows down before it wraps.
const CategoryContributionCard = () => {
    const dispatch = useDispatch();
    const getCustomSalesGrcVendorDashboardData = useSelector(state => state?.digivendStore?.getCustomSalesGrcVendorDashboardData || {});

    const [chartData, setChartData] = useState([]);
    const [totalQty, setTotalQty] = useState(0);

    useEffect(() => {
        dispatchHandler(dispatch, "getCustomSalesGrcVendorDashboardRequest", { 
            type: "CATEGORY", 
            filter: {} 
        });
    }, [dispatch]);

    useEffect(() => {
        if (getCustomSalesGrcVendorDashboardData?.isSuccess) {
            const responseBody = getCustomSalesGrcVendorDashboardData?.data?.data || getCustomSalesGrcVendorDashboardData?.data || {};
            const resource = responseBody?.resource || [];
            
            let total = 0;
            const formattedData = resource.map((item, index) => {
                const value = Number(item?.sales_qty) || 0;
                total += value;
                return {
                    name: item?.category || 'Unknown',
                    value: value,
                    percentage: item?.share_pct != null ? `${item?.share_pct}%` : "0%",
                    color: DONUT_COLORS[index % DONUT_COLORS.length]
                };
            });
            
            setChartData(formattedData);
            setTotalQty(total);
        }
    }, [getCustomSalesGrcVendorDashboardData?.isSuccess, getCustomSalesGrcVendorDashboardData?.data]);

    return (
        <div className={`bg-white border border-borderSlateGrey rounded-[10px] p-[12px] ${getCustomSalesGrcVendorDashboardData?.isLoading ? 'opacity-50 pointer-events-none' : ''}`}>
            <div className='flex items-start justify-between gap-3'>
                <p className='mb-0 capitalize text-[16px] font-semibold text-fontBlack'>
                    <LanguageTranslator>Category Contribution</LanguageTranslator>
                    <span>(By Sales Qty)</span>
                </p>
                <div className='bg-[#F0F2F5] rounded-[3px] p-[4px] flex items-center gap-[4px] shrink-0'>
                    <Info size={16} color='#5D5FEF' strokeWidth={1.6} />
                    <LanguageTranslator tag='p' className='mb-0 text-[12px] font-medium leading-[1.4] text-fontGrey whitespace-nowrap'>Category Filters Available</LanguageTranslator>
                </div>
            </div>

            <div className='mt-[8px] flex flex-col lg:flex-row items-center gap-[16px]'>
                <div className='relative w-full lg:w-[240px] h-[220px] shrink-0'>
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                            <Pie
                                data={chartData}
                                dataKey="value"
                                innerRadius="58%"
                                outerRadius="98%"
                                startAngle={90}
                                endAngle={-270}
                                paddingAngle={1}
                                stroke="none"
                                isAnimationActive={false}
                            >
                                {chartData?.map(item => <Cell key={item?.name} fill={item?.color} />)}
                            </Pie>
                        </PieChart>
                    </ResponsiveContainer>

                    <div className='absolute inset-0 flex flex-col items-center justify-center pointer-events-none'>
                        <LanguageTranslator tag='p' className='mb-0 capitalize text-[13px] font-medium leading-[1.2] text-fontBlack text-center max-w-[80px]'>Total Sales Qty</LanguageTranslator>
                        <p className='mb-0 mt-[6px] text-[20px] font-semibold text-black'>{totalQty}</p>
                    </div>
                </div>

                <div className='flex-1 min-w-0 w-full grid grid-cols-1 sm:grid-cols-2 sm:grid-rows-5 sm:grid-flow-col gap-x-[24px] gap-y-[10px]'>
                    {chartData?.map(item => (
                        <div key={item?.name} className='flex items-center gap-[25px] px-[10px] py-[4px] rounded-[10px]'>
                            <div className='flex items-center gap-[8px] w-[87px] shrink-0'>
                                <span className='size-[10px] rounded-full border border-borderSlateGrey shrink-0' style={{ backgroundColor: item?.color }} />
                                <LanguageTranslator tag='p' className='mb-0 flex-1 min-w-0 capitalize text-[11px] font-normal text-[#4A5D7A] truncate'>{item?.name}</LanguageTranslator>
                            </div>
                            <p className='mb-0 w-[40px] shrink-0 text-[16px] font-semibold text-[#111D31]'>{item?.value}</p>
                            <p className='mb-0 w-[40px] shrink-0 text-[16px] font-medium text-[#111D31]'>{item?.percentage}</p>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    )
}

export default CategoryContributionCard
